import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { LoggingService } from '@logging/logging.service';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';

/** Response from GET /auth/reset-password/validate — drives whether the DOB field is shown. */
export interface ResetPasswordValidationResponse {
  requiresDateOfBirth: boolean;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  /** ISO date string (YYYY-MM-DD). Only sent when the token requires DOB verification. */
  dateOfBirth?: string;
}

@Injectable()
export class ResetPasswordService {
  protected apiRoot = `${environment.apiPaths.authentication}/reset-password`;

  constructor(
    protected logger: LoggingService,
    private httpClient: HttpClient,
    private authenticationState: ActiveAuthenticationStateService
  ) {}

  /** Checks whether this reset token requires date-of-birth verification after an email change. */
  validateResetPasswordToken$(token: string): Observable<ResetPasswordValidationResponse> {
    // Do not toggle authenticationState.inFlight — validate can be cancelled by takeUntilDestroyed
    // on navigation away, and the Create Password button already gates on tokenValidationSettled.
    const params = new HttpParams().set('token', token);
    return this.httpClient.get<ResetPasswordValidationResponse>(`${this.apiRoot}/validate`, { params }).pipe(
      tap((response) => {
        this.logger.trace('RESET PASSWORD SERVICE: token validation response: ', response);
      })
    );
  }

  requestResetPassword$(email: string): Observable<void> {
    this.authenticationState.inFlight = true;
    return this.httpClient.post<void>(`${this.apiRoot}/request`, { email }).pipe(
      tap((response) => {
        this.logger.trace('RESET PASSWORD SERVICE: requested response: ', response);
      }),
      finalize(() => {
        this.authenticationState.inFlight = false;
      })
    );
  }

  resetPassword$(token: string, password: string, dateOfBirth?: string): Observable<void> {
    this.authenticationState.inFlight = true;
    const body: ResetPasswordRequest = {
      token,
      password,
      ...(dateOfBirth ? { dateOfBirth } : {})
    };
    return this.httpClient.post<void>(`${this.apiRoot}`, body).pipe(
      tap((response) => {
        this.logger.trace('RESET PASSWORD SERVICE: reset response: ', response);
      }),
      finalize(() => {
        this.authenticationState.inFlight = false;
      })
    );
  }
}
